import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerUser, loginUser, signInWithGoogle } from '../services/authService';

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();

  const handleGoogleSignIn = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
      navigate('/game');
    } catch (err) {
      setError(err.message || 'Google sign-in failed');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await loginUser(email, password);
      } else {
        if (!username.trim()) {
          setError('Username is required');
          setLoading(false);
          return;
        }
        await registerUser(email, password, username);
      }
      navigate('/game');
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background dark:bg-dark-background dotted-bg flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-10 animate-fadeInDown">
          <div className="mb-4 flex justify-center">
            <img src="/gemini-logo.svg" alt="Gemini Logo" width="80" height="80" />
          </div>
          <div className="text-4xl font-bold text-text-primary dark:text-dark-text-primary mb-3">
            MasterGemi
          </div>
          <p className="text-text-secondary dark:text-dark-text-secondary text-base">
            Challenge the AI Chess Master powered by Google Gemini
          </p>
        </div>
        
        {/* Form Card */}
        <div className="bg-background dark:bg-dark-surface border-2 border-border dark:border-dark-border p-8 shadow-brutal dark:shadow-brutal-dark">
          {/* Toggle */}
          <div className="flex mb-6 gap-2">
            <button 
              type="button"
              className={`flex-1 py-3 border-2 font-medium text-sm transition-all duration-150 ${
                isLogin 
                  ? 'bg-accent dark:bg-dark-accent text-background dark:text-dark-background border-border-heavy dark:border-dark-border-heavy' 
                  : 'bg-transparent text-text-primary dark:text-dark-text-primary border-border dark:border-dark-border hover:bg-hover-bg dark:hover:bg-dark-hover-bg'
              }`}
              onClick={() => setIsLogin(true)}
            >
              Login
            </button>
            <button 
              type="button"
              className={`flex-1 py-3 border-2 font-medium text-sm transition-all duration-150 ${
                !isLogin 
                  ? 'bg-accent dark:bg-dark-accent text-background dark:text-dark-background border-border-heavy dark:border-dark-border-heavy' 
                  : 'bg-transparent text-text-primary dark:text-dark-text-primary border-border dark:border-dark-border hover:bg-hover-bg dark:hover:bg-dark-hover-bg'
              }`}
              onClick={() => setIsLogin(false)}
            >
              Register
            </button>
          </div>
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div>
                <label className="block text-text-primary dark:text-dark-text-primary text-sm font-medium mb-2">
                  Username
                </label>
                <input 
                  type="text"
                  className="tool-input"
                  placeholder="Choose a username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required={!isLogin}
                />
              </div>
            )}
            
            <div>
              <label className="block text-text-primary dark:text-dark-text-primary text-sm font-medium mb-2">
                Email
              </label>
              <input 
                type="email"
                className="tool-input"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            <div>
              <label className="block text-text-primary dark:text-dark-text-primary text-sm font-medium mb-2">
                Password
              </label>
              <input 
                type="password"
                className="tool-input"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            
            {error && (
              <div className="bg-surface-dark dark:bg-dark-surface-dark border-2 border-border-heavy dark:border-dark-border-heavy p-4">
                <p className="text-text-primary dark:text-dark-text-primary text-sm font-medium">{error}</p>
              </div>
            )}
            
            <button 
              type="submit"
              className="btn btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || googleLoading}
            >
              {loading ? 'Please wait...' : (isLogin ? 'Login' : 'Create Account')}
            </button>
          </form>
          
          {/* Divider */}
          <div className="flex items-center my-6">
            <div className="flex-1 border-t border-border dark:border-dark-border"></div>
            <span className="px-4 text-text-secondary dark:text-dark-text-secondary text-sm">or</span>
            <div className="flex-1 border-t border-border dark:border-dark-border"></div>
          </div>
          
          {/* Google Sign In Button */}
          <button 
            type="button"
            onClick={handleGoogleSignIn}
            disabled={loading || googleLoading}
            className="w-full py-3 px-4 border-2 border-border dark:border-dark-border bg-white dark:bg-dark-surface hover:bg-gray-50 dark:hover:bg-dark-hover-bg transition-all duration-150 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed shadow-brutal dark:shadow-brutal-dark hover:translate-x-0.5 hover:translate-y-0.5"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="font-medium text-text-primary dark:text-dark-text-primary">
              {googleLoading ? 'Signing in...' : 'Continue with Google'}
            </span>
          </button>
        </div>
        
        {/* Footer Note */}
        <p className="text-center text-text-tertiary dark:text-dark-text-tertiary text-xs mt-6">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default Login;
